import { LocalAudioTrack } from 'livekit-client';
import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Hls from 'hls.js';
import {
  getStore,
  usePlayerStore,
  useExamSessionStore,
} from '../../../stores/StoreContext';
import { View } from '@instructure/ui';
import { Parser } from 'm3u8-parser';
import moment from 'moment';
import CONSTANTS from '../../../config/constants';

const HlsVideoPlayer = forwardRef(
  (
    { url, startTime = 0, endTime, onEndTime, hlsConfig = {}, videoProps = {}, showRawVideo = false, onBufferingStart, onBufferingEnd, onPlaying, onSuggestedSpeedChange },
    ref
  ) => {
    const videoRef = useRef(null);
    const hlsInstanceRef = useRef(null);

    const PlayerStore = getStore('playerStore');
    const ExamSessionStore = getStore('examSessionStore');

    const [audioTrack, setAudioTrack] = useState(null);

    const playbackSpeed = usePlayerStore((state) => state.playbackSpeed);
    const currentPlaybackTime = usePlayerStore((state) => state.currentTime);
    const playing = usePlayerStore((state) => state.playing);
    const recordingStatus = useExamSessionStore(
      (state) => state.recordingStatus
    );

    class RawLoader extends Hls.DefaultConfig.loader {
      load(context, config, callbacks) {
        super.load(context, config, {
          onSuccess: (response, stats, ctx, xhr) => {
            if (
              typeof response.data === 'string' &&
              recordingStatus !== CONSTANTS.RECORDING_STATUS.COMPLETE
            ) {
              var parser = new Parser();
              parser.push(response.data);
              parser.end();

              var parsedManifest = parser.manifest;

              // update the recording end date if the HLS file is still being updated...
              if (!parsedManifest.endList) {
                const lastSegment =
                  parsedManifest.segments[parsedManifest.segments.length - 1];
                const recordingEndDate = moment
                  .utc(lastSegment.dateTimeString)
                  .add(lastSegment.duration, 'seconds')
                  .toISOString();
                ExamSessionStore.setRecordingMetadata({
                  endDate: recordingEndDate,
                });
              }
            }
            // then hand back to hls.js to parse as usual:
            callbacks.onSuccess(response, stats, ctx, xhr);
          },
          onError: callbacks.onError,
          onTimeout: callbacks.onTimeout,
          onProgress: callbacks.onProgress,
        });
      }
    }

    const isPlaying = () => {
      const video = videoRef.current;
      if (!video) {
        return false;
      }
      if (!video.paused && !video.ended && video.readyState > 2) {
        return true;
      } else {
        return false;
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        play: () => videoRef.current?.play(),
        pause: () => videoRef.current?.pause(),
        setPlaybackRate: (rate) => {
          if (videoRef.current) {
            videoRef.current.playbackRate = rate;
          }
        },
        loadSource: (newUrl) => {
          if (Hls.isSupported() && hlsInstanceRef.current) {
            hlsInstanceRef.current.loadSource(newUrl);
          } else if (videoRef.current) {
            videoRef.current.src = newUrl;
          }
        },
        // Expose the hls.js instance
        getHlsInstance: () => hlsInstanceRef.current,
      }),
      []
    );

    useEffect(() => {
      const video = videoRef.current;

      // Validate URL before attempting to load
      if (!url || url.includes('undefined') || url.includes('null')) {
        console.error('HlsVideoPlayer: Invalid URL provided:', url);
        return;
      }

      // Set the HLS Video in the player store so it can be used in other places...
      PlayerStore.setHlsVideoRef(videoRef);

      const handleTimeUpdate = () => {
        const currentSecond = Math.floor(video.currentTime);
        const videoDuration = Math.ceil(video.duration);
        // TODO: Make sure this is not calling too much...
        if (isPlaying()) {
          PlayerStore.setCurrentTime(currentSecond);
        }
      };

      /**
       * Handle video ended event - ensures final time is set even at high playback speeds
       * This fires when the video naturally ends, regardless of timeupdate frequency
       */
      const handleEnded = () => {
        const videoDuration = Math.ceil(video.duration);

        PlayerStore.setPlaying(false);
        PlayerStore.setCurrentTime(videoDuration); // Set to exact duration
        video.pause();
        if (onEndTime) {
          onEndTime();
        }
      };

      /**
       * Handle seeked event - fires when seeking operation completes
       * Clear the seeking state when video is ready to play at new position
       */
      const handleSeeked = () => {
        // Always clear seeking state when seeked event fires
        PlayerStore.setIsSeeking(false);
      };

      /**
       * Handle playing event - fires when video actually starts playing
       * This is critical for clearing buffer overlays that may be stuck
       */
      const handlePlaying = () => {
        if (onPlaying) {
          onPlaying({
            currentTime: video.currentTime,
            playbackRate: video.playbackRate
          });
        }
      };

      if (Hls.isSupported()) {
        const finalConfig = {
          startPosition: startTime,
          loader: RawLoader,
          // Aggressive buffering for high-speed playback
          maxBufferLength: 90,              // 90 seconds forward buffer
          maxMaxBufferLength: 180,          // Allow up to 180 seconds
          maxBufferSize: 100 * 1000 * 1000, // 100MB buffer
          backBufferLength: 10,             // Keep 10s back buffer
          frontBufferFlushThreshold: 90,    // Don't flush until 90s

          // Fragment loading optimization
          maxFragLookUpTolerance: 0.25,
          maxLoadingDelay: 2,               // Reduce delay to 2s
          fragLoadingTimeOut: 20000,        // 20s timeout
          fragLoadingMaxRetry: 6,
          fragLoadingMaxRetryTimeout: 64000,

          // Enable prefetching
          startFragPrefetch: true,          // Start prefetching next fragment early
          testBandwidth: true,              // Test bandwidth for optimization

          // ABR optimization for quality
          abrEwmaDefaultEstimate: 5000000,  // 5Mbps default estimate
          abrBandWidthFactor: 0.95,
          abrBandWidthUpFactor: 0.7,

          // Performance
          enableWorker: true,               // Use web worker
          lowLatencyMode: false,            // Prioritize buffer over latency
          progressive: true,

          // Auto-start
          autoStartLoad: true,
          startLevel: -1,                   // Auto-select quality

          ...hlsConfig,
        };
        const hls = new Hls(finalConfig);
        hlsInstanceRef.current = hls;
        // Hide all video controls
        video.controls = false;

        hls.loadSource(url);
        hls.attachMedia(video);

        // Coordinated prefetch system
        let prefetchInterval = null;
        const hlsLoadingSegments = new Set(); // Track what HLS.js is loading
        const prefetchedSegments = new Set(); // Track what we've prefetched
        const activePrefetches = new Set(); // Track active prefetch requests
        const MAX_CONCURRENT = 6;
        let totalSegments = null; // Track total segments from manifest

        // Buffer monitoring for pause/resume
        let bufferMonitorInterval = null;
        let isBuffering = false;
        let wasPlayingBeforeBuffer = false;
        const BUFFER_CHECK_INTERVAL = 500; // Check buffer every 500ms
        const MIN_BUFFER_SECONDS = 30; // Minimum buffer needed at high speeds
        const TARGET_BUFFER_SECONDS = 60; // Target buffer before resuming

        // Network performance tracking for dynamic speed suggestions
        const segmentLoadTimes = []; // Track recent segment load times
        const MAX_LOAD_TIME_SAMPLES = 10; // Keep last 10 segment load times
        let lastBufferCheck = { time: 0, bufferAhead: 0 };
        let currentSuggestedSpeed = null;
        let speedSuggestionInterval = null;

        // Track HLS.js fragment loading to avoid conflicts
        hls.on(Hls.Events.FRAG_LOADING, (event, data) => {
          if (data.frag && data.frag.url) {
            hlsLoadingSegments.add(data.frag.url);
            // Track load start time
            data.frag.loadStartTime = performance.now();
          }
        });

        hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
          if (data.frag && data.frag.url) {
            hlsLoadingSegments.delete(data.frag.url);

            // Track segment load time for performance analysis
            if (data.frag.loadStartTime) {
              const loadTime = performance.now() - data.frag.loadStartTime;
              segmentLoadTimes.push(loadTime);

              // Keep only recent samples
              if (segmentLoadTimes.length > MAX_LOAD_TIME_SAMPLES) {
                segmentLoadTimes.shift();
              }
            }
          }
        });

        const calculateOptimalSpeed = () => {
          const currentSpeed = typeof playbackSpeed === 'string' ? parseFloat(playbackSpeed) : playbackSpeed;

          // Don't suggest speeds if we don't have enough data yet
          if (segmentLoadTimes.length < 3 || !video.duration) return;

          const currentTime = video.currentTime;
          const buffered = video.buffered;

          // Calculate current buffer ahead
          let bufferAhead = 0;
          if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
              if (buffered.start(i) <= currentTime && buffered.end(i) > currentTime) {
                bufferAhead = buffered.end(i) - currentTime;
                break;
              }
            }
          }

          // Calculate average segment load time (in ms)
          const avgLoadTime = segmentLoadTimes.reduce((a, b) => a + b, 0) / segmentLoadTimes.length;
          const segmentDuration = 5000; // 5 seconds in ms

          // Calculate buffer growth rate (how fast buffer is growing/shrinking)
          const now = performance.now();
          let bufferGrowthRate = 0;
          if (lastBufferCheck.time > 0) {
            const timeDelta = (now - lastBufferCheck.time) / 1000; // Convert to seconds
            const bufferDelta = bufferAhead - lastBufferCheck.bufferAhead;
            bufferGrowthRate = bufferDelta / timeDelta; // Seconds of buffer per second
          }
          lastBufferCheck = { time: now, bufferAhead };

          // Calculate network capacity: how many seconds of video we can download per second
          // If a 5-second segment takes 500ms to download, we can download 10 seconds of video per second
          const downloadCapacity = segmentDuration / avgLoadTime;

          // Calculate sustainable speed based on network capacity
          // We want to leave some headroom (80% of capacity) to avoid buffering
          const networkBasedSpeed = Math.floor(downloadCapacity * 0.8);

          // Factor in buffer health
          let bufferBasedSpeed;
          if (bufferAhead > 90) {
            // Excellent buffer - can go max speed
            bufferBasedSpeed = 16;
          } else if (bufferAhead > 60) {
            // Good buffer - can go high speed
            bufferBasedSpeed = 12;
          } else if (bufferAhead > 40) {
            // Moderate buffer - medium-high speed
            bufferBasedSpeed = 10;
          } else if (bufferAhead > 30) {
            // Low buffer - reduce speed
            bufferBasedSpeed = 8;
          } else {
            // Critical buffer - slow down significantly
            bufferBasedSpeed = 4;
          }

          // Factor in buffer growth rate
          let growthBasedSpeed;
          if (bufferGrowthRate > 2) {
            // Buffer growing fast - can increase speed
            growthBasedSpeed = 16;
          } else if (bufferGrowthRate > 0.5) {
            // Buffer growing - maintain or increase
            growthBasedSpeed = 12;
          } else if (bufferGrowthRate > -0.5) {
            // Buffer stable - maintain current speed
            growthBasedSpeed = currentSpeed;
          } else if (bufferGrowthRate > -2) {
            // Buffer shrinking - reduce speed
            growthBasedSpeed = Math.max(4, currentSpeed - 2);
          } else {
            // Buffer shrinking fast - reduce significantly
            growthBasedSpeed = 4;
          }

          // Take the minimum of all factors to be conservative
          let suggestedSpeed = Math.min(networkBasedSpeed, bufferBasedSpeed, growthBasedSpeed);

          // Clamp to valid range (1x to 16x)
          suggestedSpeed = Math.max(1, Math.min(16, suggestedSpeed));

          // Round to common speeds: 1, 2, 4, 8, 10, 12, 16
          const commonSpeeds = [1, 2, 4, 8, 10, 12, 16];
          suggestedSpeed = commonSpeeds.reduce((prev, curr) =>
            Math.abs(curr - suggestedSpeed) < Math.abs(prev - suggestedSpeed) ? curr : prev
          );

          // Only notify if speed suggestion changed
          if (suggestedSpeed !== currentSuggestedSpeed) {
            const previousSuggestion = currentSuggestedSpeed;
            currentSuggestedSpeed = suggestedSpeed;

            // Call callback if provided
            if (onSuggestedSpeedChange) {
              onSuggestedSpeedChange({
                suggestedSpeed,
                currentSpeed,
                previousSuggestion,
                metrics: {
                  bufferAhead,
                  bufferGrowthRate,
                  avgLoadTime,
                  downloadCapacity: downloadCapacity.toFixed(1),
                  networkBasedSpeed,
                  bufferBasedSpeed,
                  growthBasedSpeed
                }
              });
            }
          }
        };

        const monitorBuffer = () => {
          const speed = typeof playbackSpeed === 'string' ? parseFloat(playbackSpeed) : playbackSpeed;

          // Only monitor at high speeds (8x and above)
          if (speed < 8 || !video.duration) {
            return;
          }

          const currentTime = video.currentTime;
          const buffered = video.buffered;
          const remainingTime = video.duration - currentTime;

          // Calculate available buffer ahead
          let bufferAhead = 0;
          if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
              if (buffered.start(i) <= currentTime && buffered.end(i) > currentTime) {
                bufferAhead = buffered.end(i) - currentTime;
                break;
              }
            }
          }

          // Adjust buffer requirements if we're near the end of the video
          // Don't trigger buffering if there's less than MIN_BUFFER_SECONDS remaining
          const effectiveMinBuffer = Math.min(MIN_BUFFER_SECONDS, remainingTime);
          const effectiveTargetBuffer = Math.min(TARGET_BUFFER_SECONDS, remainingTime);

          // Check if we need to start buffering
          // Don't buffer if we're within 5 seconds of the end
          if (!isBuffering && bufferAhead < effectiveMinBuffer && !video.paused && remainingTime > 5) {
            isBuffering = true;
            wasPlayingBeforeBuffer = !video.paused;

            // Pause video
            video.pause();

            // Call user's callback for showing overlay
            if (onBufferingStart) {
              onBufferingStart({ bufferAhead, speed, targetBuffer: effectiveTargetBuffer });
            } else {
              console.warn('[Buffer Monitor] onBufferingStart callback not provided!');
            }
          }

          // Check if we can resume playback
          if (isBuffering && bufferAhead >= effectiveTargetBuffer) {
            isBuffering = false;

            // Resume if we were playing before
            if (wasPlayingBeforeBuffer) {
              video.play().catch(err => console.error('Failed to resume:', err));
            }

            // Call user's callback for hiding overlay
            if (onBufferingEnd) {
              onBufferingEnd({ bufferAhead, speed });
            }
          }
        };

        const coordinatedPrefetch = () => {
          const speed = typeof playbackSpeed === 'string' ? parseFloat(playbackSpeed) : playbackSpeed;

          // Only prefetch at high speeds and if we know total segments
          if (speed < 8 || !video.duration || !totalSegments) return;

          const currentTime = video.currentTime;
          const segmentDuration = 5;
          const currentSeg = Math.floor(currentTime / segmentDuration);

          // Find where HLS.js buffer ends
          const buffered = video.buffered;
          let hlsBufferEnd = currentTime;
          if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
              if (buffered.start(i) <= currentTime && buffered.end(i) > currentTime) {
                hlsBufferEnd = buffered.end(i);
                break;
              }
            }
          }

          const hlsBufferEndSeg = Math.floor(hlsBufferEnd / segmentDuration);

          // Start prefetching well beyond HLS.js buffer
          // Add 10 segment safety margin to avoid conflicts with HLS.js native loading
          const prefetchStartSeg = Math.max(hlsBufferEndSeg + 10, currentSeg + 20);

          // Don't prefetch if we're already near the end
          if (prefetchStartSeg >= totalSegments) {
            return;
          }

          // Prefetch count based on speed, but cap at remaining segments
          const rawPrefetchCount = speed >= 16 ? 60 : (speed >= 12 ? 45 : 30);
          const prefetchCount = Math.min(rawPrefetchCount, totalSegments - prefetchStartSeg);

          const levels = hls.levels;
          if (!levels || levels.length === 0) return;

          const levelUrl = levels[hls.currentLevel]?.url?.[0] || levels[0]?.url?.[0];
          if (!levelUrl) return;

          const baseUrl = levelUrl.substring(0, levelUrl.lastIndexOf('/') + 1);

          // Prefetch segments beyond HLS.js buffer
          let segmentsQueued = 0;
          for (let i = 0; i < prefetchCount; i++) {
            const segNum = prefetchStartSeg + i;

            // Double-check we haven't exceeded total segments
            if (segNum >= totalSegments) {
              break;
            }

            const segUrl = `${baseUrl}segment_${String(segNum).padStart(5, '0')}.ts`;

            // Skip if HLS.js is loading it, we've prefetched it, or we're prefetching it
            if (hlsLoadingSegments.has(segUrl) ||
              prefetchedSegments.has(segUrl) ||
              activePrefetches.has(segUrl)) {
              continue;
            }

            // Limit concurrent prefetches
            if (activePrefetches.size >= MAX_CONCURRENT) break;

            activePrefetches.add(segUrl);
            segmentsQueued++;

            fetch(segUrl, { priority: 'low' })
              .then(response => {
                if (response.ok) {
                  prefetchedSegments.add(segUrl);
                } else {
                  console.warn(`[Prefetch] Failed to load segment ${segNum}: ${response.status}`);
                }
                activePrefetches.delete(segUrl);

                // Limit cache size - remove oldest prefetched segments
                if (prefetchedSegments.size > 100) {
                  const first = prefetchedSegments.values().next().value;
                  prefetchedSegments.delete(first);
                }
              })
              .catch((err) => {
                console.warn(`[Prefetch] Error loading segment ${segNum}:`, err.message);
                activePrefetches.delete(segUrl);
              });
          }
        };

        const handleVideoReady = () => {
          PlayerStore.setHlsPlaybackReady(true);
        };

        hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          try {
            //video.play();
          } catch (e) {
            console.warn(
              'Cannot autoplay video until user interacts with the document.'
            );
          }

          // Calculate total segments from manifest data
          if (data.levels && data.levels.length > 0) {
            const level = data.levels[0];
            if (level.details && level.details.fragments) {
              totalSegments = level.details.fragments.length;
              console.log(`[Manifest] Total segments from manifest: ${totalSegments}`);
            }
          }

          // Fallback: Calculate from video duration
          if (!totalSegments && video.duration) {
            const segmentDuration = 5; // 5 second segments
            totalSegments = Math.ceil(video.duration / segmentDuration);
            console.log(`[Manifest] Total segments calculated from duration: ${totalSegments} (duration: ${video.duration.toFixed(1)}s)`);
          }

          // Start coordinated prefetch - runs every 300ms
          prefetchInterval = setInterval(coordinatedPrefetch, 300);

          // Start buffer monitor - runs every 500ms
          bufferMonitorInterval = setInterval(monitorBuffer, BUFFER_CHECK_INTERVAL);

          // Start speed suggestion calculator - runs every 2 seconds
          speedSuggestionInterval = setInterval(calculateOptimalSpeed, 2000);
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            console.error('[HLS] Fatal error:', data);
          }
        });

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('seeked', handleSeeked);
        video.addEventListener('playing', handlePlaying);
        video.addEventListener('loadeddata', handleVideoReady);
        video.addEventListener('loadedmetadata', handleVideoReady);
        video.addEventListener('canplay', handleVideoReady);

        return () => {
          video.removeEventListener('timeupdate', handleTimeUpdate);
          video.removeEventListener('ended', handleEnded);
          video.removeEventListener('seeked', handleSeeked);
          video.removeEventListener('playing', handlePlaying);
          video.removeEventListener('loadeddata', handleVideoReady);
          video.removeEventListener('loadedmetadata', handleVideoReady);
          video.removeEventListener('canplay', handleVideoReady);
          if (prefetchInterval) {
            clearInterval(prefetchInterval);
          }
          if (bufferMonitorInterval) {
            clearInterval(bufferMonitorInterval);
          }
          if (speedSuggestionInterval) {
            clearInterval(speedSuggestionInterval);
          }
          hls.destroy();
        };
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        video.currentTime = startTime;

        video.addEventListener('loadedmetadata', () => {
          video.play();
        });

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('seeked', handleSeeked);
        video.addEventListener('playing', handlePlaying);
        return () => {
          video.removeEventListener('timeupdate', handleTimeUpdate);
          video.removeEventListener('ended', handleEnded);
          video.removeEventListener('seeked', handleSeeked);
          video.removeEventListener('playing', handlePlaying);
        };
      }
    }, []);
    //[url , startTime, endTime, hlsConfig, onEndTime]
    useEffect(() => {
      const video = videoRef.current;

      if (playbackSpeed !== video.playbackRate) {
        // if the playback rate is 100.0, this means we are in smart mode, load as 1 to start...
        if (playbackSpeed === '100.0') {
          video.playbackRate = 1;
        } else {
          video.playbackRate = playbackSpeed;
        }

        if (playing && !isPlaying()) {
          // Check if video has a valid source before trying to play
          const hasValidSource = video.src || (hlsInstanceRef.current && hlsInstanceRef.current.media);
          if (hasValidSource) {
            video.play().catch((error) => {
              console.error('HlsVideoPlayer: Failed to play video after playback speed change:', error);
            });
          }
        }
      }
    }, [playbackSpeed]);

    useEffect(() => {
      const video = videoRef.current;

      if (playing && !isPlaying()) {
        // Check if video has a valid source before trying to play
        const hasValidSource = video.src || (hlsInstanceRef.current && hlsInstanceRef.current.media);

        if (!hasValidSource) {
          console.warn('HlsVideoPlayer: Cannot play - no valid source attached yet');
          return;
        }

        try {
          // Capture at 60fps for smooth high-speed playback
          const mediaStream = video.captureStream(60);
          const audioTrack = mediaStream.getAudioTracks()[0];

          // Only create LocalAudioTrack if audioTrack exists
          if (audioTrack) {
            const localAudio = new LocalAudioTrack(audioTrack, {
              name: 'hls-audio',
            });
            setAudioTrack(localAudio);
          }
        } catch (error) {
          console.warn('Failed to capture audio stream:', error);
        }

        video.play().catch((error) => {
          console.error('HlsVideoPlayer: Failed to play video:', error);
        });
        //requestAnimationFrame(drawVideos);
      } else if (!playing && isPlaying()) {
        video.pause();
      }
    }, [playing]);

    return (
      <video
        ref={videoRef}
        style={showRawVideo ? { width: '100%', height: '100%' } : { height: '0px', width: '0px' }}
        {...videoProps}
      />
    );
  }
);

export default HlsVideoPlayer;
